import { Link, router } from 'expo-router';
import { FileQuestionIcon, PlusIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';

import { MemberHeaderBar } from '@/components/member-header-bar';
import { RouteSelector } from '@/components/routes/route-selector';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { memberTenantsForUser } from '@/lib/tenant';
import { BRAND } from '@sixam/config/brand';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { cn } from '@sixam/ui/utils';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

export default function RoutesScreen() {
  const { isAdmin } = useSession();
  const gpxImportEnabled = useReleaseFeatureFlag('gpxImport') === true;
  const routePlanningEnabled = useReleaseFeatureFlag('routePlanning') === true;
  const routeSearchEnabled = useReleaseFeatureFlag('routeSearch') === true;
  const { activeTenant, isTenantLoading, memberships, tenants } = useTenant();
  const [query, setQuery] = React.useState('');
  const availableTenants = React.useMemo(
    () => memberTenantsForUser(tenants, memberships, isAdmin),
    [isAdmin, memberships, tenants]
  );
  const defaultTenantId =
    availableTenants.find((tenant) => tenant.id === activeTenant?.id)?.id ??
    availableTenants[0]?.id ??
    null;
  const [selectedTenantId, setSelectedTenantId] = React.useState<string | null>(defaultTenantId);
  const effectiveTenantId = availableTenants.some((tenant) => tenant.id === selectedTenantId)
    ? selectedTenantId
    : defaultTenantId;
  const selectedTenant = availableTenants.find((tenant) => tenant.id === effectiveTenantId) ?? null;

  function createRoute() {
    if (!selectedTenant) return;
    router.push({ pathname: '/app/routes/new', params: { tenant: selectedTenant.slug } });
  }

  return (
    <View className="flex-1 bg-background" collapsable={false}>
      <MemberHeaderBar
        onSearchChange={routeSearchEnabled ? setQuery : undefined}
        searchPlaceholder="Search routes"
      />
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        contentContainerClassName="mx-auto w-full max-w-4xl gap-5 px-4 pb-28 pt-4"
      >
        <View className="gap-1 border-b border-border pb-4">
          <Text variant="label" className="text-muted-foreground">
            Route library
          </Text>
          <Text variant="h1">Club routes</Text>
          <Text variant="muted">Save GPX routes here, then attach them when you create a run.</Text>
        </View>

        {selectedTenant ? (
          <View className="flex-row flex-wrap gap-3">
            {routePlanningEnabled ? (
              <Button onPress={createRoute}>
                <Icon as={PlusIcon} className="size-4 text-primary-foreground" />
                <Text>Create route</Text>
              </Button>
            ) : null}
            {gpxImportEnabled ? (
              <Link href="/app/routes/import-guide" asChild>
                <Button variant="secondary">
                  <Icon as={FileQuestionIcon} className="size-4" />
                  <Text>GPX guide</Text>
                </Button>
              </Link>
            ) : null}
          </View>
        ) : null}

        {isTenantLoading ? (
          <View className="items-center py-12">
            <ActivityIndicator color={BRAND.color.orange} />
          </View>
        ) : null}

        {!isTenantLoading && availableTenants.length === 0 ? (
          <View className="border-y border-border py-10">
            <Text variant="large">Join a club to create routes</Text>
            <Text variant="muted">Saved routes belong to a club route library.</Text>
          </View>
        ) : null}

        <RouteTenantPicker
          availableTenants={availableTenants}
          onSelect={setSelectedTenantId}
          selectedTenantId={effectiveTenantId}
        />

        {selectedTenant ? (
          <RouteSelector
            key={selectedTenant.id}
            mode="library"
            query={query}
            tenantId={selectedTenant.id}
          />
        ) : null}
      </ScrollView>
    </View>
  );
}

function RouteTenantPicker({
  availableTenants,
  onSelect,
  selectedTenantId,
}: Readonly<{
  availableTenants: ReturnType<typeof memberTenantsForUser>;
  onSelect: (tenantId: string) => void;
  selectedTenantId: string | null;
}>) {
  if (availableTenants.length === 1) {
    return (
      <Text variant="label" className="text-muted-foreground">
        {availableTenants[0].name}
      </Text>
    );
  }
  if (availableTenants.length < 2) return null;

  return (
    <ScrollView horizontal contentContainerClassName="gap-2" showsHorizontalScrollIndicator={false}>
      {availableTenants.map((tenant) => {
        const selected = tenant.id === selectedTenantId;
        return (
          <Pressable
            key={tenant.id}
            accessibilityRole="radio"
            accessibilityState={{ checked: selected }}
            className={cn(
              'min-h-11 justify-center rounded-full border px-4 active:opacity-70',
              selected ? 'border-primary bg-primary' : 'border-border bg-background'
            )}
            onPress={() => onSelect(tenant.id)}
          >
            <Text className={selected ? 'font-bold text-primary-foreground' : 'font-bold'}>
              {tenant.name}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
